// src/pages/PMDashboardPage.tsx

import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthAdapter';
import { SidebarPortal } from '../../shared/components/SidebarPortal';
import { Project } from '../services/api/types';
import { apiService } from '../services/api';
import { useApi } from '../hooks/useApi';
import CreateProjectModal from '../components/CreateProjectModal';
import EditProjectModal from '../components/EditProjectModal';
import ProjectSettingsModal from '../components/ProjectSettingsModal';
import ProjectDetailsGrid from '../components/project-form/ProjectDetailsGrid';
import WeeklyCalendarView from '../components/WeeklyCalendarView';
import ManagerWeeklyCalendarView from '../components/ManagerWeeklyCalendarView';
import CreateTaskModal from '../components/CreateTaskModal';
import ConfirmationModal from '../components/ConfirmationModal';
import { Button, IconButton, Input, Spinner, SkeletonList, EmptyState } from '../../shared/components/ui';

const PMDashboardPage: React.FC = () => {
  try {
    const { user } = useContext(AuthContext);
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingProject, setEditingProject] = useState<Project | null>(null);
    const [settingsProject, setSettingsProject] = useState<Project | null>(null);
    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);

    // Confirmation modal states
    const [confirmationModal, setConfirmationModal] = useState<{
      isOpen: boolean;
      type: 'complete' | 'delete' | null;
      project: Project | null;
    }>({
      isOpen: false,
      type: null,
      project: null
    });
    const [taskModalData, setTaskModalData] = useState<{
      employeeId: number;
      employeeName: string;
      date: Date;
      projectId: number;
    } | null>(null);
    const [currentWeek, setCurrentWeek] = useState(getStartOfWeek(new Date()));
    const [viewMode, setViewMode] = useState<'overview' | 'project'>('overview');
    const [projectQuery, setProjectQuery] = useState('');

  // Use API hook for projects
  const {
    data: projects,
    loading: projectsLoading,
    error: projectsError,
    execute: fetchProjects
  } = useApi(
    () => apiService.projects.getProjects(),
    [],
    {}
  );

  // Auto-select first project when projects load
  useEffect(() => {
    if (projects && Array.isArray(projects) && projects.length > 0 && !selectedProject) {
      setSelectedProject(projects[0]);
    }
  }, [projects, selectedProject]);

  const handleProjectClick = (project: Project) => {
    setSelectedProject(project);
    setViewMode('project');
  };

  const handleBackToOverview = () => {
    setViewMode('overview');
    setSelectedProject(null);
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newWeek = new Date(currentWeek);
    newWeek.setDate(newWeek.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentWeek(newWeek);
  };

  const handleProjectCreated = () => {
    setIsModalOpen(false);
    fetchProjects(); // Refresh projects list
  };

  const handleProjectUpdated = (updated: Project) => {
    setEditingProject(null);
    fetchProjects(); // Refresh projects list
    if (selectedProject?.id === updated.id) {
      setSelectedProject((prev) => (prev ? { ...prev, ...updated } : updated));
    }
  };

  // Confirmation modal handlers
  const handleCompleteProject = (project: Project) => {
    setConfirmationModal({
      isOpen: true,
      type: 'complete',
      project: project
    });
  };

  const handleDeleteProject = (project: Project) => {
    setConfirmationModal({
      isOpen: true,
      type: 'delete',
      project: project
    });
  };

  const handleConfirmAction = async () => {
    if (!confirmationModal.project) return;

    try {
      if (confirmationModal.type === 'complete') {
        await apiService.projects.completeProject(confirmationModal.project.id);
      } else if (confirmationModal.type === 'delete') {
        await apiService.projects.deleteProject(confirmationModal.project.id);
      }

      // Refresh projects list
      fetchProjects();

      // Clear selection if this was the selected project
      if (selectedProject?.id === confirmationModal.project.id) {
        setSelectedProject(null);
      }
    } catch (error) {
      console.error(`Error ${confirmationModal.type}ing project:`, error);
      alert(`Error ${confirmationModal.type === 'complete' ? 'completing' : 'deleting'} project. Please try again.`);
    }
  };

  const closeConfirmationModal = () => {
    setConfirmationModal({
      isOpen: false,
      type: null,
      project: null
    });
  };

  const handleCreateTask = (employeeId: number, employeeName: string, date: Date) => {
    // Find the project that this employee is assigned to
    const employeeProject = projectsList.find(project =>
      project.assigned_employee_ids && Array.isArray(project.assigned_employee_ids) && project.assigned_employee_ids.includes(employeeId)
    );

    if (!employeeProject) {
      return;
    }

    setTaskModalData({
      employeeId,
      employeeName,
      date,
      projectId: employeeProject.id
    });
    setIsTaskModalOpen(true);
  };

  const handleTaskCreated = () => {
    setIsTaskModalOpen(false);
    setTaskModalData(null);
    // Optionally refresh data or show success message
  };

  const projectsList = Array.isArray(projects) ? projects : [];
  const filteredProjects = projectQuery.trim()
    ? projectsList.filter((p: Project) => p.name.toLowerCase().includes(projectQuery.trim().toLowerCase()))
    : projectsList;

  const canManageProjects = !!(user?.role && (
    ['manager', 'director', 'admin', 'project manager'].includes(user.role.toLowerCase()) ||
    user.role.toLowerCase().includes('manager') ||
    user.role.toLowerCase().includes('director') ||
    user.role.toLowerCase().includes('admin') ||
    user.role === 'Project Manager'
  ));

  return (
    <>
      <SidebarPortal>
      {/* Only show Create New Project button for managers and admins */}
      {canManageProjects && (
        <Button
          variant="primary"
          fullWidth
          className="mb-4"
          onClick={() => setIsModalOpen(true)}
          data-testid="project-create-button"
          leftIcon={
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
          }
        >
          Novo Projeto
        </Button>
      )}

      <div className="mb-4">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-foreground-muted">
          Projetos {projectsLoading ? '(...)' : `(${projectsList.length})`}
        </h3>
        {!projectsLoading && projectsList.length > 6 && (
          <Input
            type="search"
            placeholder="Buscar projeto..."
            value={projectQuery}
            onChange={(e) => setProjectQuery(e.target.value)}
            containerClassName="mb-3"
            className="h-9"
            data-testid="project-search-input"
            leftIcon={
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            }
          />
        )}
        {projectsLoading ? (
          <SkeletonList rows={6} />
        ) : filteredProjects.length === 0 ? (
          <p className="px-1 py-3 text-sm text-foreground-muted">Nenhum projeto encontrado.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {filteredProjects.map((project: Project) => {
              const isSelected = selectedProject?.id === project.id;
              return (
                <li
                  key={project.id}
                  data-testid="project-row"
                  className={`flex items-center justify-between gap-2 rounded-lg border-l-[3px] px-3 py-2.5 transition-all duration-150 ${
                    isSelected
                      ? 'border-l-brand bg-brand/10'
                      : 'border-l-transparent hover:bg-surface-3'
                  }`}
                >
                  <button
                    onClick={() => handleProjectClick(project)}
                    data-testid="project-select-button"
                    className={`flex flex-1 items-center gap-2 truncate rounded text-left text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/40 ${
                      isSelected ? 'font-semibold text-brand' : 'font-medium text-foreground-muted hover:text-foreground'
                    }`}
                  >
                    {project.name}
                  </button>

                  {/* Show complete/delete buttons only if user is the project creator */}
                  {project.created_by === user?.id && (
                    <div className="flex shrink-0 items-center gap-1">
                      <IconButton
                        aria-label="Concluir projeto"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCompleteProject(project);
                        }}
                        data-testid="project-complete-button"
                        className="text-success hover:bg-success/15"
                        icon={
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6 9 17l-5-5" />
                          </svg>
                        }
                      />
                      <IconButton
                        aria-label="Deletar projeto"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteProject(project);
                        }}
                        data-testid="project-delete-button"
                        className="text-danger hover:bg-danger/15"
                        icon={
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        }
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      </SidebarPortal>

      <div className="border-b border-border bg-surface-1 px-8 py-6">
          <h1 className="font-display text-2xl font-bold text-foreground">Painel da equipe</h1>
          <p className="mt-1 text-base text-foreground-muted">
            Gerencie a agenda da equipe e acompanhe o progresso dos projetos
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border bg-surface-1 px-8 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigateWeek('prev')}
              leftIcon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              }
            >
              Semana anterior
            </Button>
            <span className="text-sm font-medium text-foreground tabular-nums">
              Semana de {currentWeek.toLocaleDateString('pt-BR')}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigateWeek('next')}
              rightIcon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              }
            >
              Próxima semana
            </Button>
          </div>

          {projectsList.length > 0 && (
            <div className="inline-flex items-center gap-1 rounded-lg border border-border bg-surface-2 p-1">
              <Button
                variant={viewMode === 'overview' ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('overview')}
                data-testid="view-overview-button"
              >
                Visão da Equipe
              </Button>
              <Button
                variant={viewMode === 'project' ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('project')}
                disabled={!selectedProject}
                data-testid="view-project-button"
              >
                Detalhe do Projeto
              </Button>
            </div>
          )}
        </div>

        <div className="px-8 py-6">
          {projectsLoading ? (
            <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
              <Spinner size="lg" />
              <h3 className="font-display text-lg font-semibold text-foreground">Carregando projetos...</h3>
            </div>
          ) : viewMode === 'overview' ? (
            projectsList.length > 0 ? (
              <ManagerWeeklyCalendarView
                projects={projectsList}
                weekStart={currentWeek}
                onCreateTask={handleCreateTask}
              />
            ) : (
              <EmptyState
                className="py-20"
                icon={
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                  </svg>
                }
                title="Nenhum projeto ainda"
                description={
                  canManageProjects
                    ? 'Crie seu primeiro projeto para começar a controlar as horas da equipe'
                    : 'Nenhum projeto disponível. Fale com seu gestor para criar projetos.'
                }
                action={
                  canManageProjects ? (
                    <Button variant="primary" onClick={() => setIsModalOpen(true)} data-testid="project-create-empty-button">
                      Criar Primeiro Projeto
                    </Button>
                  ) : undefined
                }
              />
            )
          ) : (
            selectedProject ? (
              <div>
                <div className="mb-6 rounded-xl border border-border bg-surface-1 p-6 shadow-xs">
                  <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <h3 className="font-display text-lg font-semibold text-foreground">Visão detalhada: {selectedProject.name}</h3>
                      {selectedProject.description && (
                        <p className="mt-1 text-sm text-foreground-muted">{selectedProject.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {canManageProjects && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setSettingsProject(selectedProject)}
                          data-testid="project-activity-types-button"
                          leftIcon={
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M9 11H3v10h6V11ZM21 3h-6v18h6V3ZM15 7H9v14h6V7Z" />
                            </svg>
                          }
                        >
                          Tipos de atividade
                        </Button>
                      )}
                      {canManageProjects && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setEditingProject(selectedProject)}
                          data-testid="project-configure-button"
                          leftIcon={
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          }
                        >
                          Configurar
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleBackToOverview}
                        leftIcon={
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                          </svg>
                        }
                      >
                        Voltar à Visão Geral
                      </Button>
                    </div>
                  </div>
                  <div className="border-t border-border pt-5">
                    <ProjectDetailsGrid project={selectedProject} />
                  </div>
                </div>
                <WeeklyCalendarView
                  project={selectedProject}
                  weekStart={currentWeek}
                  showHeader={false}
                />
              </div>
            ) : (
              <EmptyState
                className="py-20"
                icon={
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 11l3 3L22 4" />
                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                  </svg>
                }
                title="Selecione um projeto"
                description="Escolha um projeto na barra lateral para ver as horas detalhadas"
              />
            )
          )}
        </div>

      {isModalOpen && (
        <CreateProjectModal
          onClose={() => setIsModalOpen(false)}
          onProjectCreated={handleProjectCreated}
        />
      )}

      {editingProject && (
        <EditProjectModal
          project={editingProject}
          onClose={() => setEditingProject(null)}
          onProjectUpdated={handleProjectUpdated}
        />
      )}

      {settingsProject && (
        <ProjectSettingsModal
          project={settingsProject}
          onClose={() => setSettingsProject(null)}
        />
      )}

      {isTaskModalOpen && taskModalData && (
        <CreateTaskModal
          projectId={taskModalData.projectId}
          selectedDate={taskModalData.date}
          assignedEmployeeIds={[taskModalData.employeeId]}
          onClose={() => setIsTaskModalOpen(false)}
          onTaskCreated={handleTaskCreated}
          availableEmployees={[{ id: taskModalData.employeeId, name: taskModalData.employeeName }]}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={closeConfirmationModal}
        onConfirm={handleConfirmAction}
        title={confirmationModal.type === 'complete' ? 'Concluir Projeto' : 'Deletar Projeto'}
        message={
          confirmationModal.type === 'complete'
            ? 'Tem certeza que deseja marcar este projeto como concluído? O projeto será removido da lista de projetos ativos.'
            : 'Tem certeza que deseja deletar este projeto permanentemente? Esta ação não pode ser desfeita e todos os dados relacionados serão perdidos.'
        }
        confirmText={confirmationModal.type === 'complete' ? 'Sim, Concluir' : 'Sim, Deletar'}
        cancelText="Cancelar"
        type={confirmationModal.type === 'complete' ? 'info' : 'danger'}
        projectName={confirmationModal.project?.name}
      />
    </>
  );
  } catch (error) {
    console.error('Error in PMDashboardPage:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined,
      error: error
    });
    throw error;
  }
};

// Helper function to get start of week (Monday)
function getStartOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  return new Date(d.setDate(diff));
}

export default PMDashboardPage;
